import { User,Region } from "../../model"
import { Role } from "../../constant/role"

export enum Profile {
	SuperAdmin,
	RegionAdmin,
	LocalAuthorityAdmin,
	LocalAuthorityConsultant,
	ServiceAgent,
	Adminbudgetannexe,
	Consultantbudgeannexe,
}

export function getFullName(user: User): string {
	return `${user.firstName} ${user.lastName}`.trim()
}
export function getFullRegion(user: Region): string {
	return `${user.name}`.trim()
}

export function getProfile(user: User): Profile {
	if (hasRole(user, Role.SuperAdmin)) return Profile.SuperAdmin
	if (user.region) return Profile.RegionAdmin
	if (user.managedLocalAuthority)	return Profile.LocalAuthorityAdmin
	if (user.consultedLocalAuthority)	return Profile.LocalAuthorityConsultant
	if (user.Adminbudgetannexe || user.managedBudgetAnnexes.length)	return Profile.Adminbudgetannexe
	if (user.Consultantbudgeannexe || user.consultedBudgetAnnexes.length)	return Profile.Consultantbudgeannexe

	return Profile.ServiceAgent
}

export function getProfileLabel(profile: Profile): string {
	switch (profile) {
		case Profile.LocalAuthorityAdmin:
			return "Administrateur de collectivité"
		case Profile.LocalAuthorityConsultant:
			return "Consultant de collectivité"
		case Profile.RegionAdmin:
			return "Administrateur de région"
		case Profile.ServiceAgent:
			return "Gestionnaire"
		case Profile.SuperAdmin:
			return "Administrateur SFP"
		case Profile.Adminbudgetannexe:
			return "Administrateur budget annexe"
		case Profile.Consultantbudgeannexe:
			return "Consultant budget annexe"
		default: return "Indéterminé"
	}
}

export function getUser(users: User[], id: number): User|null {
	return users.find(u => u.id === id) || null
}

export function getUserIndex(users: User[], user: User): number|null {
	const index = users.findIndex(u => u.id === user.id)
	return -1 !== index ? index : null
}

export function getRegionIndex(users: Region[], user: Region): number|null {
	const index = users.findIndex(u => u.id === user.id)
	return -1 !== index ? index : null
}

export function hasRole(user: User, role: Role): boolean {
	return user.roles.some(userRole => userRole === role)
}

export function hasOneOfProfiles(user: User, profiles: Profile[]): boolean {
	if (!profiles || !profiles.length) return true
	if (!user) return false
	return profiles.some(p => getProfile(user) === p)
}

export function hasProfile(user: User, profile: Profile): boolean {
	const userProfile = getProfile(user)
	return profile === userProfile
}

/**
 * Trie les utilisataurs par leur prénom.
 * @param users
 * @param desc
 */
export function sortByFirstName(users: User[], desc: boolean = false): User[] {
	return users.sort((u1, u2) => {
		let comparison: -1 | 0 | 1

		// Si le premier utilisateur est défini mais pas le second
		if (u1 && !u2) comparison = 1
		// Si le premier utilisateur n'est pas défini mais le second oui
		else if (!u1 && u2) comparison = -1
		// Si aucun des utilisateurs n'est défini
		else if (!u1 && !u2) comparison = 0
		// Si le prénom du premier utilisateur est défini mais pas le second
		else if (u1.firstName && !u2.firstName) comparison = 1
		// Si le prénom du premier utilisateur n'est pas défini mais le second oui
		else if (!u1.firstName && u2.firstName) comparison = -1
		// Si aucun des prénoms des utilisateurs n'est défini
		else if (!u1.firstName && !u2.firstName) comparison = 0
		else comparison = u1.firstName.toUpperCase() >= u2.firstName.toUpperCase()
		? 1 : -1

		return desc ? - comparison : comparison
	})
}

/**
 * Trie les utilisataurs par leur nom de famille.
 * @param users
 * @param desc
 */
export function sortByLastName(users: User[], desc: boolean = false): User[] {
	return users.sort((u1, u2) => {
		let comparison: -1 | 0 | 1

		// Si le premier utilisateur est défini mais pas le second
		if (u1 && !u2) comparison = 1
		// Si le premier utilisateur n'est pas défini mais le second oui
		else if (!u1 && u2) comparison = -1
		// Si aucun des utilisateurs n'est défini
		else if (!u1 && !u2) comparison = 0
		// Si le prénom du premier utilisateur est défini mais pas le second
		else if (u1.lastName && !u2.lastName) comparison = 1
		// Si le prénom du premier utilisateur n'est pas défini mais le second oui
		else if (!u1.lastName && u2.lastName) comparison = -1
		// Si aucun des prénoms des utilisateurs n'est défini
		else if (!u1.lastName && !u2.lastName) comparison = 0
		else comparison = u1.lastName.toUpperCase() >= u2.lastName.toUpperCase()
		? 1 : -1

		return desc ? - comparison : comparison
	})
}

/**
 * Trie les utilisataurs par le libellé de leur profil.
 * @param users
 * @param desc
 */
export function sortByProfile(users: User[], desc: boolean = false): User[] {
	return users.sort((u1, u2) => {
		let comparison: -1 | 0 | 1

		// Si le premier utilisateur est défini mais pas le second
		if (u1 && !u2) comparison = 1
		// Si le premier utilisateur n'est pas défini mais le second oui
		else if (!u1 && u2) comparison = -1
		// Si aucun des utilisateurs n'est défini
		else if (!u1 && !u2) comparison = 0
		else {
			const u1Profile = getProfileLabel(getProfile(u1))
			const u2Profile = getProfileLabel(getProfile(u2))
			comparison = u1Profile >= u2Profile ? 1 : -1
		}

		return desc ? - comparison : comparison
	})
}

/**
 * Trie les utilisataurs par le libellé de leur profil.
 * @param users
 * @param desc
 */
export function sortByCollective(users: User[], desc: boolean = false): User[] {
	return users.sort((u1, u2) => {
		let comparison: -1 | 0 | 1

		if (u1 && !u2) comparison = 1
		else if (!u1 && u2) comparison = -1
		else if (!u1 && !u2) comparison = 0
		else if (u1.relatedLocalAuthority && !u2.relatedLocalAuthority) comparison = 1
		else if (!u1.relatedLocalAuthority && u2.relatedLocalAuthority) comparison = -1
		else if (!u1.relatedLocalAuthority && !u2.relatedLocalAuthority) comparison = 0
		else {
			const u1RelatedLocalAuthority = u1.relatedLocalAuthority;
			const u2RelatedLocalAuthority = u2.relatedLocalAuthority;
			comparison = (
				u1RelatedLocalAuthority && u1RelatedLocalAuthority.toUpperCase() >= u2RelatedLocalAuthority && u2RelatedLocalAuthority.toUpperCase()
				? 1 : -1
			)
		}

		return desc ? - comparison : comparison
	})
}



// WEBPACK FOOTER //
// ./src/domain/sfp/user-domain.ts